import { useScrollAnimation } from '../../hooks/useScrollAnimation';

export interface Benefit {
  icon: string;
  title: string;
  description: string;
}

interface WhyChooseUsProps {
  title?: string;
  subtitle?: string;
  benefits?: Benefit[];
}

const DEFAULT_BENEFITS: Benefit[] = [
  { icon: 'ri-award-line', title: 'Consistent, High-Quality Results', description: 'We maintain identical high standards every single visit — no shortcuts, no skipped steps. Just reliable excellence that your business can count on.' },
  { icon: 'ri-user-star-line', title: 'Trained & Vetted Professionals', description: 'Every team member undergoes comprehensive background checks, professional training, and ongoing performance evaluations before entering your facility.' },
  { icon: 'ri-calendar-2-line', title: 'Flexible Service Plans', description: 'Day cleaning, overnight, or weekend service — we build a custom plan that fits your schedule, your facilities, and your budget perfectly.' },
  { icon: 'ri-search-eye-line', title: 'Detail-Oriented Approach', description: 'We go far beyond the basics. Every corner, surface, fixture, and hard-to-reach area receives our full professional attention every single time.' },
  { icon: 'ri-time-line', title: 'Reliable Scheduling & Communication', description: 'On time, every time. We respect your business operations and communicate proactively about scheduling, staffing, and any service updates.' },
  { icon: 'ri-shield-check-line', title: 'Fully Insured & Compliant', description: 'We carry comprehensive general liability and workers\' compensation insurance, ensuring your property and people are fully protected at all times.' },
];

export default function WhyChooseUs({ title = 'Why Businesses Choose Prime Facility Services Group', subtitle = 'We don\'t just clean facilities — we protect your reputation, support your team\'s health, and maintain your property\'s value.', benefits = DEFAULT_BENEFITS }: WhyChooseUsProps) {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`grid lg:grid-cols-5 gap-14 items-start transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Sticky left label */}
          <div className="lg:col-span-2">
            <span className="inline-block px-5 py-2 bg-[#C70532]/10 text-[#C70532] text-xs font-bold uppercase tracking-widest rounded-full mb-6">Why Prime</span>
            <h2 className="text-4xl md:text-5xl font-black text-[#03143A] mb-6 leading-tight">{title}</h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">{subtitle}</p>

            {/* Mini trust stats */}
            <div className="space-y-4">
              {[
                { icon: 'ri-star-fill', value: '4.8★', label: 'Average Google Rating' },
                { icon: 'ri-group-line', value: '500+', label: 'Active Business Clients' },
                { icon: 'ri-thumb-up-line', value: '98%', label: 'Client Satisfaction Rate' },
              ].map((stat) => (
                <div key={stat.label} className="flex items-center gap-4 p-4 bg-[#FAF8F5] rounded-xl border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#C70532] rounded-xl flex-shrink-0">
                    <i className={`${stat.icon} text-white text-base`} />
                  </div>
                  <div>
                    <span className="font-black text-[#03143A] text-lg">{stat.value}</span>
                    <p className="text-xs text-gray-500">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits grid */}
          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-5">
            {benefits.map((benefit, i) => (
              <div
                key={i}
                className={`p-7 bg-[#FAF8F5] rounded-2xl border border-gray-100 hover:border-[#C70532]/20 hover:bg-white transition-all duration-400 group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#03143A] rounded-xl mb-5 group-hover:bg-[#C70532] transition-all duration-300">
                  <i className={`${benefit.icon} text-white text-xl`} />
                </div>
                <h4 className="font-bold text-[#03143A] text-base mb-2.5 leading-snug">{benefit.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
